import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, type TypesMutation } from './api';
import {
  CompositeurEffet, effetVide, obstaclesAuDepot, versEffetProjete, type EtatEffet,
} from './effet';
import { PanneauDossier } from './affaire';
import { libelleTypeMutation } from '../../src/partage/libelles';

/**
 * MUTATIONS (§14) — dépôt, instruction, double validation.
 *
 * ⚠️ CET AVERTISSEMENT A ÉTÉ INVERSÉ LE 2026-09-09, ET C'EST IMPORTANT.
 * Il annonçait « valider n'est pas appliquer », ce qui était vrai tant que
 * l'applicateur n'existait pas. Depuis que `appliquerEffet` est branché dans
 * la transaction de validation (`modules/mutations.ts`), la phrase était
 * devenue fausse — dans le sens le plus dangereux : elle disait à l'agent que
 * rien n'avait bougé alors que le registre foncier venait de changer, ce qui
 * l'invite à redéposer.
 *
 * Une mise en garde survit à ce qu'elle décrit. Quand l'état change, elle
 * ment sans qu'aucun test ne rougisse — les deux formulations sont du texte
 * valide. C'est le motif « l'étiquette décrit l'intention, pas la réalité ».
 */

const ETIQUETTE_STATUT: Record<string, string> = {
  deposee: 'ambre', instruction: 'ambre', controle_technique: 'ambre',
  attente_validation: 'bleu', validee: 'vert', rejetee: 'rouge', annulee: 'gris',
};

export function Mutations({ peutValider, onOuvrirParcelle, ouvrir }: {
  peutValider: boolean;
  onOuvrirParcelle: (id: string) => void;
  /**
   * ⚠️ LA FICHE À OUVRIR EN ARRIVANT, quand on vient du bureau. Sans elle, le
   * cockpit ne pourrait que déposer l'agent sur la LISTE — et l'acte empêché
   * qu'il venait de voir en rouge serait à retrouver à la main. Un cockpit qui
   * fait chercher ce qu'il vient de montrer n'a pas rendu service.
   */
  ouvrir?: string | null;
}) {
  const [liste, setListe] = useState<any[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<string>('');
  /**
   * §2 du cahier de navigation — le NIVEAU 2 de cet écran.
   *
   * ⚠️ LES COMPTES VIENNENT DU SERVICE, ILS NE SE CALCULENT PAS SUR `liste`.
   * La page est tronquée à 50 : compter ici rendrait « 3 ventes » sur un
   * registre qui en porte trois cents, avec l'assurance d'un total. Ils sont
   * en outre cloisonnés par le même mandat que la liste — un compteur est une
   * fuite d'information aussi sûrement qu'une liste.
   */
  const [comptes, setComptes] = useState<{ parType: Record<string, number>;
                                           parStatut: Record<string, number> }>({ parType: {}, parStatut: {} });
  const [typeFiltre, setTypeFiltre] = useState<string>('');
  const [typesConnus, setTypesConnus] = useState<string[]>([]);
  const [portee, setPortee] = useState<'territoire' | 'mandat'>('territoire');
  const [depot, setDepot] = useState(false);
  const [dossier, setDossier] = useState<string | null>(ouvrir ?? null);

  /**
   * ⚠️ `ouvrir` SUIT LES CHANGEMENTS, il n'est pas seulement une valeur
   * initiale. Deux clics successifs depuis le bureau sur deux actes différents
   * ne remontent pas l'écran : sans cet effet, le second clic n'ouvrirait rien.
   */
  useEffect(() => { if (ouvrir) setDossier(ouvrir); }, [ouvrir]);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const r = await api.mutations.liste({
        ...(filtre ? { statut: filtre } : {}),
        ...(typeFiltre ? { type: typeFiltre } : {}),
      });
      setListe(r.mutations ?? []);
      setComptes(r.comptes ?? { parType: {}, parStatut: {} });
      setPortee(r.portee ?? 'territoire');
    } catch (e) {
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas de consulter les mutations (fon.mutation:lire)."
        : String((e as Error).message ?? e));
    }
  }, [filtre, typeFiltre]);

  useEffect(() => { void charger(); }, [charger]);
  // Le catalogue des types est FIXE : on le lit une fois, pas à chaque filtre.
  useEffect(() => {
    void api.mutations.types()
      .then((t: any) => setTypesConnus((t.deposables ?? []).map((x: any) => x.type)))
      .catch(() => setTypesConnus([]));
  }, []);

  async function valider(reference: string, decision: 'approuve' | 'rejete') {
    setMessage(null); setErreur(null);
    const commentaire = window.prompt(
      decision === 'approuve'
        ? `Approuver ${reference} — motif consigné au journal :`
        : `Rejeter ${reference} — motif OBLIGATOIRE, il sera notifié au demandeur :`);
    if (commentaire === null) return;
    if (decision === 'rejete' && commentaire.trim().length < 3) { setErreur('Un rejet sans motif ne peut pas être enregistré.'); return; }
    try {
      const r = await api.mutations.valider(reference, { decision, commentaire });
      setMessage(r.message ?? `${reference} → ${r.statut} (${r.approbations} approbation(s)).`);
      await charger();
    } catch (e) {
      // Le refus des quatre yeux mérite son propre message : c'est une règle
      // métier, pas une panne, et l'agent doit savoir quoi faire ensuite.
      setErreur(e instanceof ErreurApi && e.estRefus
        ? `${e.message} — une seconde personne doit valider.`
        : String((e as Error).message ?? e));
    }
  }

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>Mutations foncières</h2>
        <div className="actions">
          <button className="bouton primaire" onClick={() => setDepot(true)}>Déposer</button>
        </div>
      </header>

      {/**
        * §2 DU CAHIER DE NAVIGATION — LE NIVEAU 2 DE CET ÉCRAN.
        *
        * ⚠️ DEUX AXES QUI NE SE CONFONDENT PAS : le STATUT dit où en est
        * l'acte dans le travail, le TYPE dit ce qu'il fait juridiquement. Les
        * mélanger en une seule liste — « Ventes, Validées, Donations, À
        * signer » — obligerait l'agent à deviner lequel des deux il filtre.
        */}
      <nav className="onglets">
        <button type="button" className={`bouton ${filtre === '' ? 'actif' : 'discret'}`}
          onClick={() => setFiltre('')}>
          Toutes <span className="jeton">{Object.values(comptes.parStatut).reduce((a, b) => a + b, 0)}</span>
        </button>
        {Object.keys(ETIQUETTE_STATUT).map((st) => (
          <button key={st} type="button" className={`bouton ${filtre === st ? 'actif' : 'discret'}`}
            onClick={() => setFiltre(st)}>
            {st} <span className="jeton">{comptes.parStatut[st] ?? 0}</span>
          </button>
        ))}
      </nav>

      {/**
        * ⚠️ LES TYPES À ZÉRO RESTENT AFFICHÉS, ET CE N'EST PAS DU BRUIT.
        * « Aucune mainlevée en cours » est une information ; une entrée
        * escamotée fait conclure que la fonction n'existe pas, et l'agent va
        * la chercher ailleurs. C'est la règle du rail des rubriques — affiché,
        * et disant ce qu'il en est — appliquée aux filtres.
        */}
      {typesConnus.length > 0 && (
        <nav className="onglets-marche">
          <button type="button" className={typeFiltre === '' ? 'actif' : undefined}
            onClick={() => setTypeFiltre('')}>Tous les types</button>
          {typesConnus.map((t) => (
            <button key={t} type="button" className={typeFiltre === t ? 'actif' : undefined}
              onClick={() => setTypeFiltre(t)}>
              {libelleTypeMutation(t)} <span className="jeton">{comptes.parType[t] ?? 0}</span>
            </button>
          ))}
        </nav>
      )}

      {/**
        * ⚠️ CE QUI EST RESTREINT SE DIT. Une liste courte et une liste
        * cloisonnée sont indiscernables à l'écran : sans cette phrase, le
        * notaire conclurait qu'il n'y a rien, jamais qu'on ne lui montre que
        * le sien. La portée vient du SERVICE, qui seul sait ce qu'il a filtré.
        */}
      {portee === 'mandat' && (
        <p className="note-indispo">
          Vous ne voyez que les actes qui relèvent de votre mandat. Les compteurs ci-dessus
          suivent la même règle : ils ne comptent pas ceux de vos confrères.
        </p>
      )}

      <div className="avertissement">
        <strong>La seconde approbation INSCRIT au registre.</strong> Deux voix distinctes
        clôturent l'instruction <em>et</em> appliquent l'effet dans la même opération :
        radiation des droits visés, inscription des nouveaux, création des titulaires.
        Il n'y a pas d'étape ultérieure — au moment où vous approuvez en second,
        le registre foncier change.
      </div>

      {message && <p className="succes" role="status">{message}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {depot && <FormulaireDepot onFerme={() => setDepot(false)} onDepose={() => { setDepot(false); void charger(); }} />}

      {/* ⚠️ LE DOSSIER S'OUVRE EN PLACE DE LA LISTE, pas à côté. Les deux
          ensemble tiendraient sur un écran de bureau et sur aucun autre, et
          l'agent perdrait de vue la seule chose qui compte à ce moment : ce
          qui bloque CE dossier. */}
      {dossier && (
        <PanneauDossier
          reference={dossier}
          onFerme={() => setDossier(null)}
          onChange={() => { void charger(); }}
        />
      )}

      {dossier ? null : liste.length === 0 ? (
        <p className="vide">Aucune mutation{filtre ? ` au statut « ${filtre} »` : ''}.</p>
      ) : (
        <table className="tableau large">
          <thead><tr><th>Référence</th><th>Type</th><th>Parcelle</th><th>Statut</th><th>Approb.</th><th>Déposée</th><th /></tr></thead>
          <tbody>
            {liste.map((m) => (
              <tr key={m.reference}>
                <td>
                  <button className="lien" onClick={() => setDossier(m.reference)}
                          title={`Ouvrir le dossier ${m.reference}`}>
                    <code>{m.reference}</code>
                  </button>
                </td>
                <td>{m.type}</td>
                <td>{m.parcelle
                  ? <button className="lien" onClick={() => onOuvrirParcelle(m.parcelle)}><code>{m.parcelle}</code></button>
                  : '—'}</td>
                <td><span className={`etat ${ETIQUETTE_STATUT[m.statut] ?? 'gris'}`}>{m.statut}</span></td>
                <td>{m.approbations ?? 0}/2</td>
                <td>{m.depose_le ? new Date(m.depose_le).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="actions-ligne">
                  {peutValider && !['validee', 'rejetee', 'annulee'].includes(m.statut) && (
                    <>
                      <button className="bouton discret" onClick={() => valider(m.reference, 'approuve')}>Approuver</button>
                      <button className="bouton discret danger" onClick={() => valider(m.reference, 'rejete')}>Rejeter</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * DÉPÔT D'UN ACTE — au guichet sur un dossier, ou hors dossier.
 *
 * ⚠️ EXPORTÉ POUR ÊTRE OUVERT DEPUIS LE DOSSIER. C'est le maillon qui manquait :
 * le guichet ouvrait une demande et son dossier, et aucun écran ne savait y
 * rattacher l'acte. Un second formulaire dans l'écran Dossiers aurait été une
 * seconde version du compositeur d'effet — celle qu'on ne teste pas.
 *
 * ⚠️ ET LE RATTACHEMENT EST DIT À L'ÉCRAN, dans les deux sens. Un acte déposé
 * hors dossier n'a ni demande, ni récépissé, ni pièces : ce n'est pas un défaut,
 * c'est un autre chemin — mais l'agent doit savoir lequel il emprunte au moment
 * de cliquer, pas en relisant la chronologie six mois plus tard.
 */
export function FormulaireDepot({ onFerme, onDepose, dossier }: {
  onFerme: () => void; onDepose: () => void;
  /** Quand il est fourni, l'acte s'y rattache et la parcelle est celle du dossier. */
  dossier?: { reference: string; parcelle: string | null; typeSuggere?: string | null };
}) {
  const [types, setTypes] = useState<TypesMutation | null>(null);
  const [type, setType] = useState<string>(dossier?.typeSuggere ?? 'immatriculation');
  const [parcelle, setParcelle] = useState(dossier?.parcelle ?? '');
  const [motif, setMotif] = useState('');
  const [effet, setEffet] = useState<EtatEffet>(effetVide());
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => { api.mutations.types().then(setTypes).catch(() => setTypes(null)); }, []);

  const exigences = types?.deposables.find((d) => d.type === type);
  const obstacles = obstaclesAuDepot(effet, exigences);

  // ⚠️ La case « radier » suit le TYPE choisi, mais reste modifiable. On
  // pré-remplit selon ce que le service exige — on ne décide pas à sa place :
  // une radiation imposée en silence écrirait une extinction que l'agent n'a
  // pas voulue.
  useEffect(() => {
    if (exigences) setEffet((e) => ({ ...e, radier: exigences.radiationAttendue }));
  }, [exigences?.type]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      await api.mutations.deposer({
        type, identifiantParcelle: parcelle.trim(),
        effetProjete: versEffetProjete(effet), motif: motif.trim(),
        ...(dossier ? { dossierReference: dossier.reference } : {}),
      });
      onDepose();
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : String(err));
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire large" onSubmit={soumettre}>
      <h3>Enregistrer une opération sur une parcelle</h3>

      {/**
        * ⚠️ CE MENU AFFICHAIT LES VALEURS BRUTES DE L'ÉNUMÉRATION — « donation »,
        * « inscription_surete » — alors que `src/partage/libelles.ts` porte les
        * libellés depuis toujours. Signalé par le fondateur : « le parcours
        * cadastral est incompréhensible, où fait-on la donation ». L'opération
        * ÉTAIT là ; elle s'écrivait dans le vocabulaire de la base.
        */}
      <label>Que voulez-vous enregistrer ?
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {(types?.deposables ?? []).map((d) => (
            <option key={d.type} value={d.type}>{libelleTypeMutation(d.type)}</option>
          ))}
        </select>
      </label>
      {/**
        * ⚠️ LA NOTE ÉTAIT REÇUE ET JETÉE. Le serveur explique, pour CHAQUE type,
        * ce que l'opération fait au registre — « le droit du cédant est radié et
        * celui de l'acquéreur inscrit dans la même opération ». L'écran la
        * récupérait dans `types.deposables[].note` et ne l'affichait nulle part.
        * C'est précisément ce qui rendait le parcours illisible : il fallait
        * savoir d'avance ce qu'une donation déclenche pour oser la choisir.
        */}
      {(() => {
        const choisi = (types?.deposables ?? []).find((d) => d.type === type);
        if (!choisi) return null;
        return (
          <p className="aide-type">
            <strong>{libelleTypeMutation(choisi.type)}</strong> — {choisi.note}
            <span className="aide-exigences">
              {choisi.droitsAttendus && <b>droits à inscrire</b>}
              {choisi.radiationAttendue && <b>droit à radier</b>}
              {choisi.contrepartieAttendue && <b>prix et date d'acte</b>}
            </span>
          </p>
        );
      })()}

      {/* ⚠️ LES TYPES NON CONSTRUITS SONT DITS, PAS CACHÉS. Ils figurent au
          cahier des charges ; les faire disparaître laisserait croire qu'ils
          n'existent pas. Les PROPOSER serait pire : le dépôt réussirait, la
          seconde validation échouerait, et la parcelle resterait verrouillée
          par une mutation qui ne peut plus aboutir. */}
      {types && types.nonConstruits.length > 0 && (
        <p className="note-indispo">
          Non déposables pour l'instant : {types.nonConstruits.map((n) => n.type).join(', ')}.
          {' '}{types.nonConstruits[0]?.raison}
        </p>
      )}

      <label>Parcelle
        <input value={parcelle} onChange={(e) => setParcelle(e.target.value)}
               placeholder="GN-PAR-… ou GN-M25-…" required />
      </label>

      <CompositeurEffet etat={effet} onChange={setEffet} exigences={exigences} />

      <label>Motif <span className="aide">consigné au journal, non modifiable ensuite</span>
        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} minLength={3} required />
      </label>

      {obstacles.length > 0 && (
        <div className="avertissement danger">
          <strong>Ce dossier ne peut pas être déposé :</strong>
          <ul>{obstacles.map((o, k) => <li key={k}>{o}</li>)}</ul>
        </div>
      )}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      <div className="actions">
        <button type="button" className="bouton discret" onClick={onFerme}>Annuler</button>
        <button className="bouton primaire"
                disabled={envoi || parcelle.trim().length < 3 || motif.trim().length < 3 || obstacles.length > 0}>
          {envoi ? 'Dépôt…' : 'Déposer'}
        </button>
      </div>
    </form>
  );
}
